import { createRouter, createWebHistory } from "vue-router";
import authRoutes from "./auth"; // Import your auth routes
import devRoutes from "./dev";
import appRoutes from "./app";
import authService from '@/services/auth';
import inactivityService from '@/services/inactivity';

const publicRouteNames = [
  'Login',
  'ForgotPassword',
  'ConfirmMail',
  'ResetPassword',
  'OTP',
  'LoginOTP',
  'LockScreen',
  'Root',
  'SuperAdminLogin',
  'Test',
  'SocialCallback',
  'MailError403',
  'MailError404',
  'MailError419',
  'MailError500',
  'MailError503',
  'MailErrorCatchAll',
];

const isPublicRoute = (route) => publicRouteNames.includes(route?.name);

const routes = [
  ...authRoutes,
  ...appRoutes,
  ...devRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: "MailErrorCatchAll",
    component: () => import("../views/errors/MailErrorView.vue"),
    props: { status: 404 },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Global auth guard with inactivity tracking
router.beforeEach((to, from, next) => {
  const isPublic = isPublicRoute(to);
  const isAuthed = authService.isAuthenticated();
  const isLocked = isAuthed && authService.isSessionLocked();
  const currentUser = authService.getCurrentUser();
  const isSuperAdmin = !!currentUser?.is_super_admin;
  const authedHome = isSuperAdmin ? 'SuperAdminDashboard' : 'CustomerMailWorkspace';

  // Check if user needs 2FA verification
  const requires2FA = authService.requires2FAVerification();
  
  // Initialize inactivity tracking for authenticated users
  if (isAuthed && !isPublic) {
    inactivityService.init();
  } else if (isPublic) {
    // Stop inactivity tracking on public pages
    inactivityService.stop();
  }

  // Not authenticated - redirect to login
  if (!isPublic && !isAuthed) {
    const loginName = to.meta?.requiresSuperAdmin ? 'SuperAdminLogin' : 'Login';
    return next({ name: loginName, replace: true, query: { redirect: to.fullPath } });
  }

  if (!isAuthed && to.name === 'LockScreen') {
    return next({ name: 'Login', replace: true });
  }

  if (isAuthed && to.name === 'LockScreen' && !isLocked) {
    const redirectPath = from?.fullPath && from.fullPath !== '/lock-screen' ? from.fullPath : (isSuperAdmin ? '/super-admin' : '/mail/inbox');
    authService.lockSession(redirectPath);
  }

  if (isLocked && to.name !== 'LockScreen' && to.name !== 'SocialCallback') {
    return next({ name: 'LockScreen', replace: true });
  }

  if (to.meta?.requiresSuperAdmin && !isSuperAdmin) {
    return next({ name: 'MailError403', replace: true });
  }

  if (to.name === 'SuperAdminLogin' && isAuthed && isSuperAdmin) {
    return next({ name: 'SuperAdminDashboard', replace: true });
  }

  if (to.name === 'SuperAdminLogin' && isAuthed && !isSuperAdmin) {
    return next({ name: 'CustomerMailWorkspace', replace: true });
  }

  if (to.meta?.requiresCustomer && isAuthed && isSuperAdmin) {
    return next({ name: 'MailError403', replace: true });
  }

  if (to.meta?.requiresCompanyAdmin) {
    const isCompanyAdmin = !!currentUser?.is_company_admin || !!currentUser?.is_company_owner;
    if (!isCompanyAdmin) {
      return next({ name: 'MailError403', replace: true });
    }
  }

  if (to.name === 'CustomerMailApp' && String(to.params?.application || '').toLowerCase() === 'team') {
    const isCompanyAdmin = !!currentUser?.is_company_admin || !!currentUser?.is_company_owner;
    if (!isCompanyAdmin) {
      return next({ name: 'MailError403', replace: true });
    }
  }

  // Authenticated but needs 2FA verification - only allow LoginOTP and logout
  if (isAuthed && requires2FA && to.name !== 'LoginOTP') {
    return next({ name: 'LoginOTP', replace: true });
  }

  // Authenticated, 2FA completed, but trying to access LoginOTP - redirect to dashboard
  if (isAuthed && !requires2FA && to.name === 'LoginOTP') {
    return next({ name: authedHome, replace: true });
  }

  // Authenticated and trying to access guest-only pages like Login/Register - redirect to dashboard
  if (isPublic && isAuthed && !requires2FA && to.name === 'Login') {
    return next({ name: authedHome, replace: true });
  }

  return next();
});

export default router;

router.onError(() => {
  if (router.currentRoute.value.name !== 'MailError500') {
    router.replace({ name: 'MailError500' });
  }
});

if (typeof window !== 'undefined') {
  window.addEventListener('mail-auth-invalid', () => {
    const currentRoute = router.currentRoute.value;
    if (!isPublicRoute(currentRoute)) {
      router.replace({
        name: 'Login',
        query: { redirect: currentRoute.fullPath || '/mail/inbox' },
      });
    }
  });
}
